import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { useAppSelector } from 'setup/store';
import { IDateFilter, IPageData, UIChipColor } from 'common/interfaces';
import UITable, { DisplayItem } from 'components/lib/UI/table/UITable';
import ReportSearchFilter from 'components/report-search-filter/ReportSearchFilter';
import { IStore } from 'common/interfaces/istore';
import { useNavigate } from 'react-router-dom';
import { getDisplayDate } from 'common/utils/date-util';
import { useRoyaltyReportActions } from 'actions/royalty-report-actions';
import { IDayReportDataStats } from 'common/interfaces/idashboard';
import { ReportStatusEnum } from 'common/constants';
import moment from 'moment';
import { getStatusChipBgColor, getStatusChipTextColor } from 'common/utils';

const DayReportStatusPage = () => {
  const { t } = useTranslation();
  const appConfig = useAppSelector((state) => state.appReducer.appConfig);
  const navigate = useNavigate();
  const error = useAppSelector((state) => state.royaltyReportReducer.error);
  const isLoading = useAppSelector((state) => state.royaltyReportReducer.loading);
  const reports = useAppSelector((state) => state.royaltyReportReducer.dayReportsStatus);
  const [_reports, setReports] = useState<IPageData<IDayReportDataStats>>();
  const [_storeNumbers, setStoreNumbers] = useState<number[]>();

  const [_selectedStore, setSelectedStore] = useState<IStore>();
  const [_selectedDateFilter, setSelectedDateFilter] = useState<IDateFilter>();
  const [headers, setHaders] = useState<DisplayItem[]>();

  // const { setSelectedStore } = useSessionActions();
  const { loadDayReportCountStats, resetContext } = useRoyaltyReportActions();

  useEffect(() => {
    resetContext();
  }, []);

  useEffect(() => {
    if (reports && reports?.stats.length > 0) {
      setReports({
        currentPage: 1,
        items: reports.stats,
        totalItems: reports.stats.length,
        totalPages: 1,
      } as IPageData<IDayReportDataStats>);
      setStoreNumbers(reports.stores || []);
      setHaders([{ label: '' }, ...reports.stores.map((st) => ({ label: `#${st}` }))]);
    } else {
      setStoreNumbers([]);
      setReports({ currentPage: 1, totalItems: 0, items: [], totalPages: 1 });
      setHaders(undefined);
    }
  }, [reports]);

  const _loadDayReports = (
    df: IDateFilter,
    selectedStore: IStore | undefined | null,
    pageNo: number,
    pageSize: number,
  ) => {
    const storeId = _selectedStore ? _selectedStore.id : -1;
    const criteria = {
      ...df,
      storeId,
    };
    return loadDayReportCountStats(selectedStore?.id || -1, criteria);
  };

  const _onSearchReport = async (dateFilter: IDateFilter, store: IStore | undefined | null) => {
    setSelectedDateFilter(dateFilter);
    await _loadDayReports(dateFilter, store, 1, appConfig.defaultListSize);
    setSelectedStore(store || undefined);
  };

  const keyExtractor = (item: IDayReportDataStats, index: number) => {
    return (item.weekEndingDate || index).toString();
  };

  const _handleRowSelection = (item: IDayReportDataStats) => {
    console.log('Item: ' + item.weekEndingDate);
  };

  const _renderRow = (item: IDayReportDataStats, index: number) => {
    const storeCount = _storeNumbers?.length || 0;
    const row = {
      id: item.weekEndingDate,
      index,
      data: [
        {
          label: item.weekEndingDate || '',
          tooltip: '',
        },
        ...Array.from({ length: storeCount }, () => ({ label: 'Not Received', tooltip: 'Not Submitted' })),
      ],
    };

    if (item.data && item.data.length > 0 && _storeNumbers) {
      item.data.forEach((dt) => {
        const idx = _storeNumbers.indexOf(dt.storeNumber);
        row.data[idx + 1] = {
          label: `${dt.status}`,
          tooltip: item.weekEndingDate + ' #' + dt.storeNumber + ' ' + dt.status,
        };
      });
    }

    return (
      <>
        {row.data.map((dt, i) => (
          <React.Fragment key={dt.label + ':' + i}>
            {i == 0 ? (
              <td
                // style={{ fontWeight: item.isBold ? 'bold' : '300', flex: item.flex || 0 }}
                key={'td:' + index + ':' + i}
              >
                <Box display="flex" flexDirection={{ xs: 'row' }} gap={2}>
                  <span style={{ width: '100%', textAlign: 'left' }}>{getDisplayDate(dt.label)}</span>
                </Box>
              </td>
            ) : (
              <td key={'td:' + index + ':' + i}>
                <Tooltip title={dt.tooltip}>
                  {/* <span style={{ width: '100%', textAlign: 'left' }}>{dt.label}</span> */}
                  {/* <Box
                    display="flex"
                    flexDirection={{ xs: 'row' }}
                    gap={2}
                    sx={{
                      height: 15,
                      width: 15, //100
                      borderRadius: '20px', //'4px',
                      backgroundColor: getBgColor(dt.label),
                    }}
                  ></Box> */}
                  <Chip
                    label={''}
                    // color={getStatusBgColor(dt.label)}
                    sx={{ backgroundColor: getStatusChipBgColor(dt.label) }}
                    size="small"
                    style={{ width: 15, height: 15, borderRadius: '20px' }}
                  />
                </Tooltip>
              </td>
            )}
          </React.Fragment>
        ))}
      </>
    );
  };
  return (
    <section>
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} className="pageTitleTitleContainer">
        <h2 className="pageTitle">{t('page.dayReportStatus')}</h2>
      </Box>
      <ReportSearchFilter handleSubmit={_onSearchReport} currentDate={moment().add(7, 'days').toDate()} duration={10} />
      <div style={{ margin: '30px 0px' }}>
        {/* {isLoading ? <LoadingSpinner /> : null} */}

        {/* <Paper> */}
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2.5} className="pageTitleTitleContainer">
          {[ReportStatusEnum.APPROVED, ReportStatusEnum.SUBMITTED, ReportStatusEnum.UNDER_REVIEW, 'NOT_SUBMITTED'].map(
            (status) => (
              <Box
                display={'flex'}
                flexDirection={{ xs: 'column', md: 'row' }}
                alignItems={'center'}
                gap={0.7}
                key={'status:' + status}
              >
                {/* <Typography textAlign={'right'} style={{ textTransform: 'capitalize' }} variant="caption">
                  {status?.toLocaleLowerCase()}
                </Typography>
                <Box
                  display="flex"
                  flexDirection={{ xs: 'row' }}
                  gap={2}
                  sx={{
                    height: 15,
                    width: 15, //100
                    borderRadius: '20px', //'4px',
                    backgroundColor: getBgColor(status),
                  }}
                /> */}
                <Chip
                  label={status}
                  // color={getStatusBgColor(status)}
                  sx={{ backgroundColor: getStatusChipBgColor(status), color: getStatusChipTextColor(status) }}
                />
              </Box>
            ),
          )}
        </Box>
        {/* </Paper> */}
        {_reports && _reports.totalItems > 0 ? (
          <UITable<IDayReportDataStats>
            pagedData={_reports}
            keyExtractor={keyExtractor}
            pageSize={appConfig.defaultListSize}
            loadData={undefined}
            renderRowItem={_renderRow}
            headers={headers}
            isLoading={isLoading}
            onItemPressed={_handleRowSelection}
            title={_selectedStore?.storeNumber ? 'Reports for Store: ' + _selectedStore?.storeNumber : ''}
            hideActions
            stickyHeader
            maxHeight={800}
          />
        ) : _reports?.totalItems === 0 ? (
          <Alert severity="warning">No Report found</Alert>
        ) : !isLoading ? (
          <Alert severity="info">Select store and date range to search for reports.</Alert>
        ) : null}
      </div>
    </section>
  );
};

export default DayReportStatusPage;
